import { action, computed, runInAction, observable, makeObservable } from "mobx";
import { contactsStore } from "Stores/ContactsStore";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { ArrayDataStore } from "Stores/RESTStoreInstances/ArrayDataStore";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { SuggestionsConfig } from "Pages/Project/QA/Components/AddCommentForm";
import { taskAttachmentsStore } from "./TaskAttachmentsStore";
import { API_URL } from "General/APIEndpoints";
import { formatUsername } from "utils/formatUsername";

class TaskCommentsStore extends ArrayDataStore<API_Comment> {
  taskId: number = null;
  buffer: API_Comment = null;

  constructor() {
    super();

    makeObservable(this, {
      taskId: observable,
      buffer: observable,
      // fetch: action.bound,
      deleteComment: action.bound,
      formatContactNumberToName: action.bound,
      formatContactNumberToUserId: action.bound,
      formatContactUserIdToNumber: action.bound,
      getCommentToEdit: action.bound,
      users: computed,
      commentsWithNames: computed,
      createComment: action.bound,
      editComment: action.bound,
    });

    this.data = [] as ArrayDataStore<API_Comment>["data"];
  }

  url(commentId?: number) {
    if (commentId) {
      return API_URL.TASKS.updateComment(projectsDataStore.projectId, this.taskId, commentId);
    }
    return API_URL.TASKS.getComments_1(projectsDataStore.projectId, this.taskId);
  }

  async fetch(taskId: number) {
    try {
      this.taskId = taskId;
      await super.fetch();
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async createComment(formData: API_Comment) {
    this.buffer = { ...this.buffer, ...formData };

    try {
      const response = await API.newData(this.url(), this.buffer);
      runInAction(() => {
        this.add(response);
        this.buffer = {};
      });
    } catch (err) {
      await taskAttachmentsStore.checkRights(err, "COMMENT_CREATE");
      // throw err;
    }
  }

  @handleStoreState()
  async editComment(formData: API_Comment) {
    this.buffer = { ...this.buffer, ...formData };

    try {
      const response = await API.editData(this.url(this.buffer.id), this.buffer);
      runInAction(() => {
        if (response) {
          this.edit(
            this.buffer.id,
            {
              ...this.find(this.buffer.id, "id"),
              ...this.buffer,
              dateChanged: new Date().getTime().toString(),
            },
            "id"
          );
        }
        this.buffer = {};
      });
    } catch (err) {
      await taskAttachmentsStore.checkRights(err, "COMMENT_EDIT");
      // throw err;
    }
  }

  async deleteComment(commentId: number) {
    try {
      await API.deleteData(this.url(commentId));
      await this.fetch(this.taskId);
    } catch (err) {
      throw err;
    }
  }

  // Converts @{userId} into User.name
  formatContactNumberToName(comment: string) {
    const mentionIds = comment.match(/@id(\d*)/g); // get all id's
    let formattedComment = comment;
    if (mentionIds !== null) {
      mentionIds.forEach((mention) => {
        const mentionId = mention.replace("@id", ""); // extract userId
        const contactObject = contactsStore.projectUsers.find((i) => i.id === +mentionId);
        const name = contactObject
          ? formatUsername({ firstName: contactObject.firstName, surname: contactObject.surname })
          : `${mentionId} (not found)`;

        const newString = formattedComment.replace(
          // replace userId with corresponding number id
          `${mention}`,
          `<b>${name}</b>`
        );
        formattedComment = newString;
      });
    }
    return formattedComment;
  }

  // Converts @id{id} into @{userId}
  formatContactNumberToUserId(comment: string) {
    const mentionIds = comment.match(/@id(\d*)/g); // get all id's
    let formattedComment = comment;
    if (mentionIds !== null) {
      mentionIds.forEach((mention) => {
        const mentionId = mention.replace("@id", ""); // extract contact id
        const contactObject = contactsStore.projectUsers.find((i) => i.id === +mentionId);
        const userId = contactObject ? contactObject.userId : `${mentionId}notFound`;

        const newString = formattedComment.replace(
          // replace userId with corresponding number id
          `@id${mentionId}`,
          `@${userId}`
        );
        formattedComment = newString;
      });
    }
    return formattedComment;
  }

  // Converts @{userId} into @id{id}
  formatContactUserIdToNumber(comment: string, mentions: string[]) {
    let formattedComment = comment;
    mentions.forEach((mention) => {
      const mentionId = mention.replace("@", ""); // extract userId
      const newString = formattedComment.replace(
        // replace userId with corresponding number id
        `@${mentionId}`,
        `@id${contactsStore.projectUsers.find((i) => i.userId === mentionId).id}`
      );
      formattedComment = newString;
    });
    return formattedComment;
  }

  getCommentToEdit(commentId: number) {
    const foundComment = this.find(commentId, "id");
    return {
      ...foundComment,
      comment: this.formatContactNumberToUserId(foundComment.comment),
    };
  }

  get users(): SuggestionsConfig {
    if (contactsStore.projectUsers) {
      return contactsStore.projectUsers
        .filter((item) => !item.restricted && item.active)
        .map((item) => ({
          label: formatUsername({ firstName: item.firstName, surname: item.surname }),
          value: item.id.toString(),
        }));
    }
    return [];
  }

  get commentsWithNames() {
    return this.data.map((item) => ({
      ...item,
      comment: item.comment ? this.formatContactNumberToName(item.comment) : null,
    }));
  }
}

const taskCommentsStore = new TaskCommentsStore();

export { taskCommentsStore, TaskCommentsStore };
